import axios from "axios";
import CODE from "../../Static/Constants/StatusCodes";
import removeAccessToken from "../../Helper/RemoveAccessToken/RemoveAccessToken";
import Logout from "../../Helper/Logout/Logout";
import { logCustomException } from "../../Helper/Common/Monitoring";

function SetNoData(msg) {
  return {
    response: {},
    responseStatus: {
      code: CODE.NODATA,
      msg: msg,
    },
  };
}

function SetForbidden(msg) {
  return {
    response: {},
    responseStatus: {
      code: CODE.FORBIDDEN,
      msg: msg,
    },
  };
}

function SetSessionExpired() {
  return {
    response: {},
    responseStatus: {
      code: CODE.SESSION_EXPIRED,
      msg: "session expired",
    },
  };
}

function callTheAPI(res, allBatteries) {
  let data = {
    response: {},
    responseStatus: {
      code: 0,
      msg: null,
    },
  };

  if (res && (res.status === CODE.SUCCESS || res.status === CODE.URL_CREATED))
    if (
      res.data.responseStatus.code === CODE.SUCCESS ||
      res.data.responseStatus.code === CODE.URL_CREATED
    ) {
      data.response = res.data;
      data.responseStatus.code = res.data.responseStatus.code;
      data.responseStatus.msg = res.data.responseStatus.msg;
      return data;
    } else if (
      res.data.responseStatus.code === 1999 ||
      res.data.responseStatus.code === 404 ||
      res.data.responseStatus.code === 1998
    ) {
      logCustomException("Error In API Call", res.data.responseStatus)

      if (!allBatteries) {
        data = SetNoData(res.data.responseStatus.msg);
        return data;
      } else {
        data.response = res.data;
        data.responseStatus.code = res.data.responseStatus.code;
        data.responseStatus.msg = res.data.responseStatus.msg;
        return data;
      }
    } else if (res.data.responseStatus.code === CODE.UNAUTHORIZED) {
      removeAccessToken();
      return null;
    } else {
      Logout();
      return null;
    }
}

async function callTheAPIRefreshingToken(refresh, config) {
  let data = {
    response: {},
    responseStatus: {
      code: 0,
      msg: null,
    },
  };
  if (refresh && refresh.status === CODE.SUCCESS) {
    try {
      let res = await axios(config);
      const data = callTheAPI(res);
      return data;
    } catch (error) {
      if (error.response.status === CODE.URL_NODATA) {
        data = SetNoData("No Data Found");
        return data;
      } else if (error.response.status === CODE.UNAUTHORIZED) {
        removeAccessToken();
        return null;
      }
    }
  }
}

async function GetDataV2(config) {
    let res = await axios(config);
    const data  = {response: {}, responseStatus: {code: 0, msg: null}};
    data.response = res?.data;
    data.responseStatus.code = res?.data?.responseStatus?.code;
    data.responseStatus.msg = res?.data?.responseStatus?.msg;

    return data
}

/**
 * Like GetData, but PRESERVES the response body on a 4xx validation failure.
 *
 * GetData copies only responseStatus.code and .msg on a 400 and discards the
 * body, which is where APIs that return per-field errors put them
 * (`data.errors`). A form cannot highlight the offending fields without it, so
 * "GST number is malformed and email is invalid" collapses into one generic
 * failure.
 *
 * Everything else -- the 401 refresh dance, subscription-expired logout, 403
 * and no-data mapping -- is delegated to GetData so there is one
 * implementation of session handling, not two.
 *
 * Returns the server's envelope verbatim ({responseStatus, data}) on success
 * and on 400/409/422; GetData's shape ({response, responseStatus}) otherwise.
 * Callers should read responseStatus.code first.
 */
// Methods that may be re-issued. A GET or a HEAD that failed can be sent again
// for free; a POST, PUT or DELETE that reached the server and then timed out
// has already been applied, and sending it a second time creates a second row.
const IDEMPOTENT_METHODS = new Set(["get", "head", "options"]);

// Statuses where the server REJECTED the request outright, so re-issuing it
// cannot duplicate any effect. 401 is the token refresh and 403 the
// subscription-expiry logout, both of which live in GetData and are worth
// keeping for writes too.
const REJECTED_BEFORE_EFFECT = new Set([401, 403]);

async function GetDataWithErrors(config) {
  try {
    const res = await axios(config);
    return res?.data;
  } catch (error) {
    const status = error.response?.status;
    // A validation failure or a conflict is an expected outcome carrying
    // structured errors, not an exception. Hand the envelope back untouched.
    if (
      (status === 400 || status === 409 || status === 422) &&
      error.response?.data?.responseStatus
    ) {
      return error.response.data;
    }

    // Delegating to GetData RE-ISSUES the request, which is how it performs the
    // 401 refresh dance. That is safe when the method is idempotent, or when
    // the server rejected the call before it could do anything. It is NOT safe
    // for a write that failed with a 500 or no response at all: the entity may
    // already exist, and a second attempt would create a duplicate.
    const method = (config?.method || "get").toLowerCase();
    if (IDEMPOTENT_METHODS.has(method) || REJECTED_BEFORE_EFFECT.has(status)) {
      return GetData(config);
    }

    // A write whose outcome is genuinely unknown. Report it rather than retry;
    // the caller shows the message and the user decides.
    return {
      response: {},
      responseStatus: {
        code: status || CODE.SERVER_ERROR,
        msg:
          error.response?.data?.responseStatus?.msg ||
          "The request could not be completed. Check whether it was saved before trying again.",
      },
    };
  }
}

async function GetData(config, allBatteries, _retries = 3) {
  let data = {
    response: {},
    responseStatus: {
      code: 0,
      msg: null,
    },
  };

  try {
    let res = await axios(config);
    const data = callTheAPI(res, allBatteries);
    return data;
  } catch (error) {
    if (error.response?.status === 400) {
      data.responseStatus.code = error.response.data?.responseStatus?.code || 400;
      data.responseStatus.msg = error.response.data?.responseStatus?.msg || "Bad Request";
      return data;
    } else if (error.response?.status === CODE.URL_NODATA) {
      data = SetNoData("No Data Found");
      return data;
    } else if (error.response?.status === CODE.FORBIDDEN) {
      data = SetForbidden(error.response.data.message);
      return data;
    } else if (error.response?.status === CODE.UNAUTHORIZED) {
      // Org subscription expired for a paid user: skip the refresh dance, log
      // out immediately, and flag the login page to show the block message.
      if (error.response?.headers?.["x-auth-reason"] === "subscription_expired") {
        sessionStorage.setItem("loginBlockedReason", "subscription");
        Logout();
        return SetSessionExpired();
      }
      try {
        let refresh = await axios.get("/api/refresh");
        const data = callTheAPIRefreshingToken(refresh, config);
        return data;
      } catch (error) {
        console.log(error?.response?.data?.responseStatus?.msg);
        if (
          error?.response?.data?.responseStatus?.code ===
            CODE.EXCEPTION_EXPIRED_REFRESH ||
          error?.response?.data?.responseStatus?.msg === CODE.ERR_MSSG
        ) {
          data = SetSessionExpired();
          return data;
        } else if (_retries > 0) {
          const data = GetData(config, allBatteries, _retries - 1);
          return data;
        } else {
          data = SetSessionExpired();
          return data;
        }
      }
    }
    if (error.response?.status == CODE.SERVER_ERROR) {
      data = SetSessionExpired();
      return data;
    }
  }
}

async function GetApiResponse(config) {
  let response = await axios(config);
  return response;
}

export { SetNoData, callTheAPI, callTheAPIRefreshingToken, GetData, GetDataV2, GetDataWithErrors, GetApiResponse };
